import { type Property, type PropertyCard as PropertyCardData } from '@resolute/schema';
import { formatConfigRange } from '@resolute/utils';

import { useFilterLabels } from '../search/use-filter-labels';

import { clientEnv } from '@/lib/env';
import { PRICE_ON_REQUEST } from '@/lib/price';
import { htmlToPlainText } from '@/lib/sanitize-html';

/**
 * Everything the detail page derives from a `Property` before rendering.
 *
 * Split out from the page component because the rules here are the substance of
 * what that page decides — which facts are real, which would be stated twice,
 * which table columns carry information — and they are far easier to reason
 * about (and to change) away from several hundred lines of JSX.
 */

export type Fact = { label: string; value: string };

/** Which per-unit table columns carry information for a given project. */
export interface UnitTableColumns {
  showTypeColumn: boolean;
  showConfigColumn: boolean;
  showSuperBuiltUpColumn: boolean;
  showUnitsColumn: boolean;
}

/**
 * Renders an area with the unit of measure the SOURCE recorded it in.
 *
 * Production records "Square Feet" throughout, which this normalises to the
 * site's usual "sq.ft". The per-unit handling is kept because CPzone does not
 * constrain the field: the retired dev environment held eight different units
 * ("1680 Square Yard", "65412 Acre", "545415 Bigha"), and labelling those
 * "sq.ft" reported Acres and Bighas as square feet. No conversion is attempted
 * — that would invent precision the source does not have — so a value is always
 * shown against its own label.
 */
export function formatAreaWithUnit(min: number, max?: number, unit?: string): string {
  const fmt = (v: number) => new Intl.NumberFormat('en-IN').format(Math.round(v));
  // "sqft"/"Square Feet" are the same unit spelled two ways upstream; normalise
  // to the site's usual notation and pass anything else through verbatim.
  const label = /^(sq\.?\s?ft|square\s?feet|foot)$/i.test(unit?.trim() ?? '')
    ? 'sq.ft'
    : unit?.trim();
  const value = max != null && max !== min ? `${fmt(min)} – ${fmt(max)}` : fmt(min);
  return label ? `${value} ${label}` : value;
}

function toCard(p: Property): PropertyCardData {
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    category: p.category,
    type: p.type,
    transaction: p.transaction,
    config: p.config,
    listingStatus: p.listingStatus,
    price: p.price,
    pricePerSqft: p.pricePerSqft,
    area: p.area,
    unitTypes: p.unitTypes,
    location: p.location,
    verified: p.verified,
    isPreLeased: p.isPreLeased,
    isGiftCity: p.isGiftCity,
    coverImage: p.media?.find((m) => m.type === 'image'),
    developerName: p.developer?.name,
  };
}

/** "Jun 2026" — month precision, since only the month is meaningful here. */
function monthYear(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

export function useDetailFacts(property: Property) {
  const labels = useFilterLabels();
  const card = toCard(property);

  const cityLabel = labels.city(property.location.city);
  const statusLabel = labels.status(property.listingStatus);
  const area = property.area.builtUp ?? property.area.carpet;

  // A project can offer several unit configs/areas/prices — `unitTypes` is
  // the full list (see UnitTypeSchema); the top-level type/config/price/area
  // fields above are just the cheapest one, so ranges are derived here rather
  // than repeating that single value everywhere.
  const unitTypes = property.unitTypes ?? [];
  const hasMultipleUnitTypes = unitTypes.length > 1;
  const configRange = formatConfigRange(
    unitTypes.length ? unitTypes.map((u) => u.config) : [property.config],
  );
  const areaValues = (
    unitTypes.length ? unitTypes.map((u) => u.area.builtUp ?? u.area.carpet) : [area]
  ).filter((v): v is number => v != null);
  /**
   * Areas are only comparable when they share a unit of measure. Production is
   * uniform ("Square Feet"), so a range is normally offered — but the guard
   * stays because the source does not enforce uniformity: one dev-environment
   * project recorded "1680 Square Yard" alongside "3600 Foot". A min–max across
   * two different units is a meaningless number, so a range is only offered when
   * every unit type agrees; otherwise the per-unit table is the only honest
   * presentation.
   */
  const areaUnits = new Set(unitTypes.map((u) => u.areaUnit ?? '').filter(Boolean));
  const singleAreaUnit = areaUnits.size === 1 ? [...areaUnits][0] : undefined;
  const areaRangeLabel =
    areaValues.length && (unitTypes.length <= 1 || singleAreaUnit)
      ? formatAreaWithUnit(Math.min(...areaValues), Math.max(...areaValues), singleAreaUnit)
      : undefined;
  const superBuiltUpValues = unitTypes
    .map((u) => u.area.superBuiltUp)
    .filter((v): v is number => v != null);
  const superBuiltUpRangeLabel =
    superBuiltUpValues.length && (unitTypes.length <= 1 || singleAreaUnit)
      ? formatAreaWithUnit(
          Math.min(...superBuiltUpValues),
          Math.max(...superBuiltUpValues),
          singleAreaUnit,
        )
      : undefined;

  /**
   * Distinct property types across the unit types, in order.
   *
   * The project's own `type` is just the CHEAPEST unit's — reporting a project
   * that offers both a Studio and an Independent Floor as simply "Studio" is
   * wrong. Listing them all is both accurate and what tells a reader which
   * types exist.
   */
  const distinctTypes = [...new Set(unitTypes.map((u) => u.type))]
    .filter(Boolean)
    .map((t) => labels.type(t));
  const typeLabel = distinctTypes.length ? distinctTypes.join(', ') : labels.type(property.type);

  /**
   * The per-unit table below carries configuration, RERA area and super
   * built-up for every unit type. When it is on screen, repeating those here as
   * project-wide ranges says the same thing twice — the table's own min/max IS
   * the range — so the summary drops them and keeps only what the table cannot
   * express: the project-level facts.
   *
   * With a single unit type there is no table, so the summary carries them.
   */
  const unitTableShown = hasMultipleUnitTypes;

  // Every row is conditional on the field actually being present upstream —
  // CPzone leaves several of these blank on some projects, and a blank is
  // omitted rather than rendered as an empty or invented value.
  const facts = [
    { label: 'Category', value: labels.category(property.category) },
    { label: distinctTypes.length > 1 ? 'Types' : 'Type', value: typeLabel },
    // Stated once for the whole listing rather than repeated down the table's
    // price column, where it was identical on every row.
    { label: 'Price', value: PRICE_ON_REQUEST },
    !unitTableShown && configRange && { label: 'Configuration', value: configRange },
    !unitTableShown && areaRangeLabel && { label: 'Built-up area', value: areaRangeLabel },
    !unitTableShown &&
      superBuiltUpRangeLabel && { label: 'Super built-up', value: superBuiltUpRangeLabel },
    { label: 'Status', value: statusLabel },
    property.furnishing && {
      label: 'Furnishing',
      value: property.furnishing.replace('-', ' '),
    },
    property.launchDate && { label: 'Launched', value: monthYear(property.launchDate) },
    property.possessionDate && { label: 'Possession', value: monthYear(property.possessionDate) },
  ].filter(Boolean) as Fact[];

  // Project-level counts, shown only where CPzone recorded them. A
  // "configurations offered" count is deliberately absent — it would just be
  // the number of rows in the table directly below.
  const inventoryFacts = [
    property.inventory?.totalBlocks != null && {
      label: 'Total blocks',
      value: String(property.inventory.totalBlocks),
    },
    property.inventory?.totalFlats != null && {
      label: 'Total flats',
      value: String(property.inventory.totalFlats),
    },
    property.inventory?.totalShops != null && {
      label: 'Total shops',
      value: String(property.inventory.totalShops),
    },
  ].filter(Boolean) as Fact[];

  /**
   * Which per-unit columns carry information for THIS project.
   *
   * `config` is empty on non-residential inventory, where the fallback was the
   * property type — identical on every row and already stated once in the
   * summary above. Super built-up is likewise absent on some projects. A column
   * that would repeat one value down every row is dropped instead.
   */
  const columns: UnitTableColumns = {
    /**
     * The property type belongs on every row whenever the project records one.
     * It is what identifies a row — "2 BHK" alone does not say whether that unit
     * is a Studio or an Independent Floor, and a project genuinely mixes them.
     * Only dropped when a single type covers the whole project, where the summary
     * above already states it.
     */
    showTypeColumn: distinctTypes.length > 1,
    showConfigColumn: unitTypes.some((u) => u.config),
    showSuperBuiltUpColumn: unitTypes.some((u) => u.area.superBuiltUp != null),
    /**
     * Unit counts are optional upstream and CPzone defaults an unset one to 0 —
     * 966 of production's ~1,020 unit types have it unset. A column of dashes
     * says nothing, so it only appears when at least one row has a real count.
     */
    showUnitsColumn: unitTypes.some((u) => Boolean(u.totalUnits)),
  };

  /**
   * Composed from whichever address parts exist — never CPzone's own
   * `full_address`, which concatenates placeholders verbatim.
   *
   * ORDER MATTERS to the layout. The two free-text fields (`Address` and the
   * nearby-areas list) are rendered full-width, so anywhere they sit in the
   * MIDDLE of this list they end their row early and strand the short field
   * after them in a row of its own — which is what left "Locality" and
   * "Country" each alone beside two empty cells. Keeping both wide fields at
   * the very start and the very end means the short fields (Locality → City →
   * State → PIN → Country, the natural address hierarchy) always pack as one
   * contiguous block, whichever of them a listing actually has.
   *
   * If another full-width field is added, put it adjacent to one of these two —
   * not between the short ones.
   */
  const nearbyAreas = property.location.nearbyAreas ?? [];
  const addressFacts = [
    property.location.address && { label: 'Address', value: property.location.address },
    { label: 'Locality', value: property.location.locality },
    { label: 'City', value: cityLabel },
    property.location.state && { label: 'State', value: property.location.state },
    property.location.pincode && { label: 'PIN code', value: property.location.pincode },
    property.location.country && { label: 'Country', value: property.location.country },
    // The listing's other tagged localities. Only `/property/:id` carries
    // these, and its response was previously fetched (to gate visibility) then
    // thrown away — so this was available all along. Last because it is
    // supplementary to the address proper, and because it is full-width.
    nearbyAreas.length > 0 && {
      label: nearbyAreas.length > 1 ? 'Nearby areas' : 'Nearby area',
      value: nearbyAreas.join(', '),
    },
  ].filter(Boolean) as Fact[];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: property.title,
      description: property.description
        ? htmlToPlainText(property.description).slice(0, 300)
        : undefined,
      url: `${clientEnv.siteUrl}/property/${property.slug}`,
      image: property.media?.filter((m) => m.type === 'image').map((m) => m.url),
      offers: {
        '@type': 'Offer',
        price: property.price,
        priceCurrency: 'INR',
        availability: ['ready-to-move', 'completed', 'possession'].includes(property.listingStatus)
          ? 'https://schema.org/InStock'
          : 'https://schema.org/PreOrder',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: clientEnv.siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Buy', item: `${clientEnv.siteUrl}/buy` },
        { '@type': 'ListItem', position: 3, name: property.title },
      ],
    },
  ];

  return {
    labels,
    card,
    cityLabel,
    unitTypes,
    hasMultipleUnitTypes,
    facts,
    inventoryFacts,
    addressFacts,
    columns,
    jsonLd,
  };
}
